<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, reactive, ref, watch, type Ref } from "vue"
import { useI18n } from "vue-i18n"
import type { Address } from "@types"
import type { OwnerOutput } from "@core/composables"
import { SOURCES_TYPES } from "@core/constants"
import { formatIdSource } from "@core/helpers"
import type { SourceExtendDialogMeter } from "@core/components/source/meter/form/extend/SourceExtendDialog.vue"
import SourceFormOwner from "@core/components/source/form/owner/SourceFormOwner.vue"

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    meter: SourceExtendDialogMeter
}>()

const emit = defineEmits<{
    cancel: []
    complete: [payload: { consentRequestId?: string; sourceId?: string }]
}>()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title_pdl: "Changement de titulaire du PDL",
            title_pce: "Changement de titulaire du PCE",
            subtitle: "{type} {id}",
            date_pce_label: "Date de la première donnée disponible",
            next: "Suivant",
            quit: "Quitter",
        },
    },
})

// ================================
// STATE
// ================================

type Phase = "edit" | "confirm" | "sent"

const phase = ref<Phase>("edit")
const owner = ref<OwnerOutput | null>(null)
const datePce = ref("")
const ownerFormRef = ref<InstanceType<typeof SourceFormOwner> | null>(null)

const sentPayload = ref<{ _id_consentrequest: string; url: string } | null>(
    null
)

// ================================
// COMPUTED
// ================================

const getIsPce = computed(
    () => String(props.meter.idtype).toLowerCase() === SOURCES_TYPES.PCE
)

const getAddress = computed((): Address => {
    const a = props.meter.address
    if (a && "street" in a) {
        return {
            street: a.street ?? "",
            zipcode: a.zipcode ?? "",
            city: a.city ?? "",
            country: a.country ?? "FR",
            citycode: a.citycode ?? null,
        }
    }
    return {
        street: "",
        zipcode: "",
        city: "",
        country: "FR",
        citycode: null,
    }
})

const getHeaderTitle = computed(() =>
    getIsPce.value ? t("title_pce") : t("title_pdl")
)

const getHeaderSubtitle = computed(() => {
    const ty = getIsPce.value ? "PCE" : "PDL"
    return t("subtitle", {
        type: ty,
        id: formatIdSource(props.meter.idsource, props.meter.idtype),
    })
})

const getIdTypeParam = computed(() => (getIsPce.value ? "pce" : "pdl"))

function readOwnerFormIsValid(
    inst: InstanceType<typeof SourceFormOwner> | null
): boolean {
    const gv = inst?.getIsValid as boolean | Ref<boolean> | undefined
    return typeof gv === "boolean" ? gv : gv != null ? gv.value : false
}

const getCanGoNext = computed(() => {
    const formOk = readOwnerFormIsValid(ownerFormRef.value)
    return (
        formOk && !!owner.value && (!getIsPce.value || !!datePce.value.trim())
    )
})

// ================================
// HANDLERS
// ================================

function onDateChange(value: Date | string | null): void {
    if (value == null) {
        datePce.value = ""
        return
    }
    const d = typeof value === "string" ? new Date(value) : value
    datePce.value = Number.isNaN(d.getTime())
        ? ""
        : d.toISOString().slice(0, 10)
}

function onClickQuit(): void {
    emit("cancel")
}

function onClickNext(): void {
    if (!getCanGoNext.value) return
    phase.value = "confirm"
}

function onConfirmBack(): void {
    phase.value = "edit"
}

function onSent(payload: { _id_consentrequest: string; url: string }): void {
    sentPayload.value = payload
    phase.value = "sent"
}

function onSentDone(): void {
    emit("complete", {
        consentRequestId: sentPayload.value?._id_consentrequest,
        sourceId: props.meter._id,
    })
}

const isStorybook = import.meta.env.STORYBOOK === "true"
const storyDebugState = isStorybook
    ? reactive<{
          phase: Phase
          owner: OwnerOutput | null
          datePce: string
          canGoNext: boolean
      }>({
          phase: "edit",
          owner: null,
          datePce: "",
          canGoNext: false,
      })
    : null

if (isStorybook && storyDebugState) {
    watch(
        [phase, owner, datePce, getCanGoNext],
        () => {
            storyDebugState.phase = phase.value
            storyDebugState.owner = owner.value
            storyDebugState.datePce = datePce.value
            storyDebugState.canGoNext = getCanGoNext.value
        },
        { immediate: true, deep: true }
    )
}

defineExpose(isStorybook && storyDebugState ? { storyDebugState } : {})
</script>

<template>
    <div class="source-change-owner-form flex flex-col gap-6 w-full max-w-3xl">
        <header v-if="phase === 'edit'" class="flex flex-col gap-2 text-center">
            <h2 class="text-lg font-semibold text-gray-800">
                {{ getHeaderTitle }}
            </h2>
            <p class="text-sm text-gray-600">
                {{ getHeaderSubtitle }}
            </p>
        </header>

        <template v-if="phase === 'edit'">
            <SourceFormOwner ref="ownerFormRef" v-model="owner" />

            <BaseInputWrapper
                v-if="getIsPce"
                unfloat
                input-id="change-owner-pce-date"
                :label="t('date_pce_label')"
            >
                <BaseDatePicker
                    :model-value="datePce ? new Date(datePce) : null"
                    date-format="dd/mm/yy"
                    show-icon
                    :max-date="new Date()"
                    icon-display="input"
                    input-id="change-owner-pce-date"
                    @update:model-value="onDateChange"
                />
            </BaseInputWrapper>

            <div class="flex flex-wrap justify-between gap-3">
                <BaseButton
                    :label="t('quit')"
                    severity="secondary"
                    class="min-w-[10rem]"
                    @click="onClickQuit"
                />
                <BaseButton
                    :label="t('next')"
                    class="min-w-[10rem]"
                    :disabled="!getCanGoNext"
                    @click="onClickNext"
                />
            </div>
        </template>

        <SourceInvitationChangeOwner
            v-else-if="phase === 'confirm' && owner"
            :source-mongo-id="meter._id"
            :address="getAddress"
            :idsource="meter.idsource"
            :type="getIdTypeParam"
            :owner="owner"
            :date-from-data="getIsPce ? datePce : null"
            @back="onConfirmBack"
            @sent="onSent"
        />

        <SourceInvitationChangeOwnerSent
            v-else-if="phase === 'sent' && sentPayload"
            :consent-request-id="sentPayload._id_consentrequest"
            :link="sentPayload.url"
            @done="onSentDone"
        />
    </div>
</template>
